import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Eye,
  Edit3,
  FileSignature,
  MapPin,
  Clock,
  Smartphone,
  ShieldCheck,
  Filter,
  RefreshCw
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { formatDistanceToNow } from 'date-fns';
import { getJson } from '@/lib/http';
import { DataTable } from '@/components/tables/data-table';
import { createProofOfPostingColumns } from '@/components/tables/proof-of-posting-columns';
import type { LiveAssignment } from '@/components/tables/proof-of-posting-columns';

import { AdminManualSignOffModal } from '@/components/proof-of-posting/AdminManualSignOffModal';
import { ApproveOperatorSubmissionModal } from '@/components/proof-of-posting/ApproveOperatorSubmissionModal';
import { ProofEditModal } from '@/components/proof-of-posting/ProofEditModal';
import { EnhancedProofModal } from '@/components/proof-of-posting/EnhancedProofModal';
import { StatusIndicator, DetailedStatusIndicator } from '@/components/proof-of-posting/StatusIndicator';
import { AdminManualSubmissionModal } from '@/components/proof-of-posting/AdminManualSubmissionModal';
import { WhatWasPostedTable } from '@/components/proof-of-posting/WhatWasPostedTable';
import {
  ComplianceDetailedGrid,
  ComplianceOverviewGrid,
  type ComplianceDetailedResponse,
} from '@/components/proof-of-posting/ComplianceDetailedGrid';
import { InstallStatusPanel } from '@/components/proof-of-posting/InstallStatusPanel';
import {
  SubmissionReviewReportPanel,
  type ReviewTaskKind,
} from '@/components/proof-of-posting/SubmissionReviewReportPanel';
import { YearFilter } from '@/components/filters/YearFilter';
import { useYear } from '@/state/yearStore';
import { useUrlYear } from '@/hooks/useUrlYear';
import { compareCyclesNewestFirst } from '@/lib/bookingCycles';

// Feature toggle for Assignment ID visibility
const SHOW_ASSIGNMENT_ID = import.meta.env.VITE_SHOW_ASSIGNMENT_ID !== 'false';

type LiveAssignmentResponse =
  | {
      data: LiveAssignment[];
      totalCount: number;
      page: number;
      pageSize: number;
    }
  | {
      assignments: LiveAssignment[];
      meta?: {
        totalAssignments?: number;
      };
    };

interface Retailer {
  id: number;
  name: string;
}

interface BookingCycle {
  id: number;
  name: string;
  retailerId: number;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}

type ApiBookingCycle = BookingCycle & {
  retailer_id?: number;
  retailer_name?: string;
  start_date?: string | Date | null;
  end_date?: string | Date | null;
  locked_flag?: boolean | null;
};

interface Operator {
  id: number;
  name: string;
  username: string;
}

// ============================================================
// Phase 2 Follow-up: Status Filter Migration Helper
// ============================================================
// Maps legacy statuses to canonical (same as backend)
const LEGACY_STATUS_MAP: Record<string, string> = {
  'sent_to_operator_mobile': 'pending',
  'sent_to_operator_pdf': 'pending',
  'sent_to_operator': 'pending',
  'ready_for_operator': 'pending',
  'admin_pending_review': 'operator_complete',
  'archived_not_actioned': 'archived',
  'archived_completed': 'archived',
  'admin_approved': 'admin_complete',
};

/**
 * Normalize legacy status values from localStorage or URL params
 * @param status - potentially legacy status value
 * @returns canonical status or 'all' if unknown
 */
function normalizeLegacyStatus(status: string | null): string {
  if (!status || status === 'all') return 'all';
  const normalized = LEGACY_STATUS_MAP[status.toLowerCase()] || status;
  // Validate against canonical list
  const canonicalStatuses = ['pending', 'operator_complete', 'admin_complete', 'admin_rejected', 'locked', 'archived'];
  return canonicalStatuses.includes(normalized) ? normalized : 'all';
}

type ProofSection = 'proof' | 'compliance' | 'reviews';

function sectionHeading(section: ProofSection): { title: string; subtitle: string } {
  switch (section) {
    case 'reviews':
      return {
        title: 'Reviews',
        subtitle: 'The approval or rejection status of operator tasks',
      };
    case 'compliance':
      return {
        title: 'Compliance',
        subtitle: 'What was expected versus what was posted, by store and campaign',
      };
    case 'proof':
      return {
        title: 'Proof of Posting',
        subtitle: 'Mobile submissions, admin sign-offs, and comprehensive proof management',
      };
    default: {
      const _exhaustive: never = section;
      return _exhaustive;
    }
  }
}

interface ProofOfPostingV2EnhancedProps {
  initialSection?: ProofSection;
}

export default function ProofOfPostingV2Enhanced({ initialSection = 'proof' }: ProofOfPostingV2EnhancedProps = {}) {
  // Global year filter
  const year = useYear();
  useUrlYear();
  
  // Check URL parameters for cycle ID (coming from notifications)
  const urlParams = new URLSearchParams(window.location.search);
  const urlCycleId = urlParams.get('cycleId');

  // Phase 2: Migrate legacy status from URL if present
  const urlStatus = urlParams.get('status');
  const migratedStatus = normalizeLegacyStatus(urlStatus);

  const [selectedRetailer, setSelectedRetailer] = useState<string>('5'); // Default to SuperValu
  const [selectedCycle, setSelectedCycle] = useState<string>(urlCycleId || ''); // Use URL cycle ID if available, otherwise empty until cycles load
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [activeSection, setActiveSection] = useState<ProofSection>(initialSection);
  const [selectedComplianceCampaign, setSelectedComplianceCampaign] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [reviewTaskKind, setReviewTaskKind] = useState<ReviewTaskKind>('both');
  const [statusFilter, setStatusFilter] = useState<string>(migratedStatus);
  const [includePending, setIncludePending] = useState<boolean>(true); // NEW: Include pending tasks by default
  const [includeArchived, setIncludeArchived] = useState<boolean>(false); // NEW: Include archived records (default: false)

  const [adminManualSignOffModal, setAdminManualSignOffModal] = useState<{
    isOpen: boolean;
    visitData?: any;
  }>({ isOpen: false });

  const [approveOperatorModal, setApproveOperatorModal] = useState<{
    isOpen: boolean;
    visitData?: any;
  }>({ isOpen: false });

  const [proofEditModal, setProofEditModal] = useState<{
    isOpen: boolean;
    visitId?: number;
  }>({ isOpen: false });

  const [enhancedProofModal, setEnhancedProofModal] = useState<{
    isOpen: boolean;
    storeId?: number;
    cycleId?: number;
    operatorId?: number;
    assignmentId?: number; // ✅ NEW: Pass specific assignment ID to filter modal
  }>({ isOpen: false });

  const [adminSubmissionModal, setAdminSubmissionModal] = useState<{
    isOpen: boolean;
    assignment?: any;
  }>({ isOpen: false });

  const queryClient = useQueryClient();

  // Fetch retailers
  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ['/api/retailers'],
    queryFn: () => getJson('/api/retailers'),
  });

  // Fetch booking cycles for selected retailer and year
  const { data: cycles = [] } = useQuery<ApiBookingCycle[], Error, BookingCycle[]>({
    queryKey: ['/api/booking-cycles', selectedRetailer, year],
    queryFn: async () => {
      if (!selectedRetailer) return [] as BookingCycle[];
      const params = new URLSearchParams();
      params.set('retailerId', selectedRetailer);
      if (year) params.set('year', String(year));
      return getJson<ApiBookingCycle[]>(`/api/booking-cycles?${params.toString()}`);
    },
    select: (data) => {
      if (!selectedRetailer) {
        return [];
      }

      const retailerIdFilter = parseInt(selectedRetailer, 10);
      if (Number.isNaN(retailerIdFilter)) {
        return [];
      }

      return data.reduce<BookingCycle[]>((acc, cycle) => {
        const retailerId = typeof cycle.retailerId === 'number'
          ? cycle.retailerId
          : typeof cycle.retailer_id === 'number'
            ? cycle.retailer_id
            : null;

        if (retailerId === retailerIdFilter) {
          acc.push({
            id: Number(cycle.id),
            name: cycle.name,
            retailerId,
            startDate: cycle.startDate ?? cycle.start_date ?? null,
            endDate: cycle.endDate ?? cycle.end_date ?? null,
          });
        }
        return acc;
      }, []).sort(compareCyclesNewestFirst);
    },
    enabled: !!selectedRetailer
  });

  // Fetch operators
  const { data: operators = [] } = useQuery<Operator[]>({
    queryKey: ['/api/users/operators'],
  });

  const isComplianceSection = activeSection === 'compliance';
  const isReviewsSection = activeSection === 'reviews';
  const needsCampaignFilter = isComplianceSection || isReviewsSection;

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  const complianceAllCampaignsQuery = useQuery<ComplianceDetailedResponse | null>({
    queryKey: ['/api/compliance/detailed', selectedRetailer, selectedCycle, 'all'],
    queryFn: async () => {
      if (!selectedRetailer || !selectedCycle) return null;
      const params = new URLSearchParams({
        retailerId: selectedRetailer,
        cycleId: selectedCycle,
      });
      return getJson<ComplianceDetailedResponse>(`/api/compliance/detailed?${params.toString()}`);
    },
    enabled: needsCampaignFilter && !!selectedRetailer && !!selectedCycle,
  });

  const complianceSingleCampaignQuery = useQuery<ComplianceDetailedResponse | null>({
    queryKey: ['/api/compliance/detailed', selectedRetailer, selectedCycle, selectedComplianceCampaign],
    queryFn: async () => {
      if (!selectedRetailer || !selectedCycle || selectedComplianceCampaign === 'all') return null;
      const params = new URLSearchParams({
        retailerId: selectedRetailer,
        cycleId: selectedCycle,
        campaignId: selectedComplianceCampaign,
      });
      return getJson<ComplianceDetailedResponse>(`/api/compliance/detailed?${params.toString()}`);
    },
    enabled: isComplianceSection && !!selectedRetailer && !!selectedCycle && selectedComplianceCampaign !== 'all',
  });

  // Auto-select the most recent cycle when cycles load (if no URL param provided)
  const hasAutoSelectedCycle = useRef(false);
  useEffect(() => {
    // Only auto-select if:
    // 1. We have cycles loaded
    // 2. No cycle is currently selected (or it's empty)
    // 3. No URL cycle ID was provided
    // 4. We haven't already auto-selected
    if (cycles.length > 0 && !selectedCycle && !urlCycleId && !hasAutoSelectedCycle.current) {
      // First cycle is newest by start date after compareCyclesNewestFirst.
      const mostRecentCycle = cycles[0];
      setSelectedCycle(mostRecentCycle.id.toString());
      hasAutoSelectedCycle.current = true;
      console.log('[PROOF-OF-POSTING] Auto-selected most recent cycle:', mostRecentCycle.name);
    }
  }, [cycles, selectedCycle, urlCycleId]);

  useEffect(() => {
    setSelectedComplianceCampaign('all');
    setSelectedStore('all');
  }, [selectedRetailer, selectedCycle]);

  useEffect(() => {
    if (selectedComplianceCampaign === 'all') return;
    const hasCampaign = (complianceAllCampaignsQuery.data?.campaigns || []).some(
      (campaign) => String(campaign.campaignId) === selectedComplianceCampaign
    );
    if (!hasCampaign && complianceAllCampaignsQuery.data) {
      setSelectedComplianceCampaign('all');
    }
  }, [selectedComplianceCampaign, complianceAllCampaignsQuery.data]);

  // Pagination state for live assignments
  const [assignmentPage, setAssignmentPage] = useState(1);
  const assignmentPageSize = 50;

  const cycleStores = useMemo(() => {
    const seen = new Map<number, string>();
    for (const row of complianceAllCampaignsQuery.data?.rows ?? []) {
      if (!seen.has(row.storeId)) {
        seen.set(row.storeId, row.storeName);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [complianceAllCampaignsQuery.data]);

  const selectedStoreId =
    selectedStore === 'all' ? null : parseInt(selectedStore, 10);

  useEffect(() => {
    if (selectedStore === 'all' || cycleStores.length === 0) return;
    if (!cycleStores.some((store) => String(store.id) === selectedStore)) {
      setSelectedStore('all');
    }
  }, [selectedStore, cycleStores]);

  const rawComplianceData = selectedComplianceCampaign === 'all'
    ? complianceAllCampaignsQuery.data
    : complianceSingleCampaignQuery.data;
  const complianceData = useMemo(() => {
    if (!rawComplianceData || selectedStoreId == null || !Number.isFinite(selectedStoreId)) {
      return rawComplianceData;
    }
    return {
      ...rawComplianceData,
      rows: rawComplianceData.rows.filter((row) => row.storeId === selectedStoreId),
    };
  }, [rawComplianceData, selectedStoreId]);
  const complianceLoading = selectedComplianceCampaign === 'all'
    ? complianceAllCampaignsQuery.isLoading
    : complianceSingleCampaignQuery.isLoading;
  const complianceError = selectedComplianceCampaign === 'all'
    ? complianceAllCampaignsQuery.error
    : complianceSingleCampaignQuery.error;

  // Fetch live assignments directly from database - replaces locked cycle dependency
  // Now with server-side pagination
  const { data: liveAssignmentData, isLoading: loadingStores, refetch: refetchStores } = useQuery<LiveAssignmentResponse | null>({
    queryKey: ['/api/live-status/assignments', selectedCycle, includeArchived, assignmentPage, assignmentPageSize, year],
    queryFn: async () => {
      if (!selectedCycle) return null;

      try {
        const params = new URLSearchParams();
        if (includeArchived) params.set('includeArchived', 'true');
        params.set('page', String(assignmentPage));
        params.set('pageSize', String(assignmentPageSize));
        params.set('year', String(year));
        const url = `/api/live-status/assignments/${selectedCycle}?${params.toString()}`;
        const data = await getJson<LiveAssignmentResponse>(url);
        console.log('Live assignment data:', data);
        return data;
      } catch (error) {
        console.error('Failed to fetch live assignment data:', error);
        return { data: [], totalCount: 0, page: 1, pageSize: assignmentPageSize };
      }
    },
    enabled: activeSection === 'proof' && !!selectedCycle,
    staleTime: 10000, // Cache for 10 seconds for performance
    refetchInterval: 15000, // Auto-refresh every 15 seconds for live updates
    placeholderData: (previousData) => previousData ?? null, // Keep previous data while loading
  });

  // Reset page when cycle or filters change
  useEffect(() => {
    setAssignmentPage(1);
  }, [selectedCycle, includeArchived]);

  // Process assignments from live API data - handle both old and new response formats
  const assignments = useMemo<LiveAssignment[]>(() => {
    if (liveAssignmentData && 'data' in liveAssignmentData && Array.isArray(liveAssignmentData.data)) {
      console.log('🔍 RAW ASSIGNMENTS FROM LIVE API (paginated):', liveAssignmentData.data.length, 'of', liveAssignmentData.totalCount, 'total');
      return liveAssignmentData.data;
    }
    if (liveAssignmentData && 'assignments' in liveAssignmentData && Array.isArray(liveAssignmentData.assignments)) {
      console.log('🔍 RAW ASSIGNMENTS FROM LIVE API (legacy):', liveAssignmentData.assignments.length, 'assignments');
      return liveAssignmentData.assignments;
    }
    return [];
  }, [liveAssignmentData]);

  // Pagination info
  const totalAssignments = liveAssignmentData && 'totalCount' in liveAssignmentData
    ? liveAssignmentData.totalCount
    : liveAssignmentData?.meta?.totalAssignments ?? assignments.length;
  const totalAssignmentPages = Math.ceil(totalAssignments / assignmentPageSize);

  // Simplified status function - using live API status directly
  const getDisplayStatus = (assignment: LiveAssignment) => {
    // If the assignment is archived, always show "Archived" regardless of other status
    if (assignment.archived) {
      return 'Archived';
    }
    return assignment.status; // Use live status directly from database
  };

  // Get cycle name from selected cycle ID
  const getCycleName = () => {
    const cycle = cycles.find(c => c.id.toString() === selectedCycle);
    return cycle ? cycle.name : selectedCycle;
  };

  // Validation function to ensure data integrity for "Signed by Operator" status
  const validateOperatorSignature = (assignment: LiveAssignment): boolean => {
    // Hard rule: Only show "Signed by Operator" if:
    // 1. Status is operator_complete (operator has submitted)
    // 2. Has posting events (evidence of submission)
    // 3. Has signature evidence asset (proof of sign-off)

    if (assignment.status !== 'operator_complete') {
      return false;
    }

    // Check for posting events
    if (!assignment.postingEvents || assignment.postingEvents.length === 0) {
      return false;
    }

    // Check for evidence assets (signatures, photos)
    const hasEvidence = assignment.mediaRequirements?.some(req =>
      req.status === 'COMPLETE' && req.eventId
    );

    return hasEvidence || false;
  };

  // Filter assignments based on selected criteria
  const filteredAssignments = useMemo(() => {
    if (!assignments || assignments.length === 0) return [];

    // DEBUG: Log live assignment status analysis
    console.log('🔍 DEBUG: Analyzing live assignment statuses...');
    const statusCounts = assignments.reduce<Record<string, number>>((counts, assignment) => {
      const status = assignment.status;
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});
    console.log(`🔍 DEBUG: Live status breakdown:`, statusCounts);

    let filtered = assignments;

    // If NOT including pending, only show assignments with evidence
    if (!includePending) {
      console.log('🔍 DEBUG: Filtering out pending assignments (only showing with evidence)...');
      const beforeCount = filtered.length;
      filtered = filtered.filter(assignment => {
        // Show assignments with evidence (either mediaRequirements delivered > 0 OR postedUnits > 0 OR hasEvidence)
        const hasEvidence = assignment.mediaRequirements?.some(media => (media.delivered ?? 0) > 0) ||
          (assignment.postedUnits ?? 0) > 0 ||
          assignment.hasEvidence ||
          assignment.postingEvents?.length > 0;

        if (!hasEvidence) {
          console.log(`🔍 DEBUG: Filtering out assignment ${assignment.id} (${assignment.storeName}) - no evidence`);
        }

        return hasEvidence;
      });
      console.log(`🔍 DEBUG: Filtered to ${filtered.length} assignments with evidence (was ${beforeCount})`);
    }
    // If including pending, show all assignments (pending + operator_complete + admin_complete)

    // Filter by operator
    if (selectedOperator && selectedOperator !== 'all') {
      console.log(`🔍 DEBUG: Filtering by operator: ${selectedOperator}`);
      const beforeCount = filtered.length;
      const operatorId = (assignment: LiveAssignment) => assignment.operatorId || assignment.userId;
      filtered = filtered.filter(assignment => operatorId(assignment) === parseInt(selectedOperator));
      console.log(`🔍 DEBUG: Filtered to ${filtered.length} assignments for operator ${selectedOperator} (was ${beforeCount})`);
    }

    // Filter by status (Phase 2: Direct comparison using canonical statuses)
    if (statusFilter && statusFilter !== 'all') {
      console.log(`🔍 DEBUG: Filtering by status: "${statusFilter}"`);
      const beforeCount = filtered.length;
      filtered = filtered.filter(assignment => {
        const status = assignment.status;
        const matches = status === statusFilter;

        if (!matches) {
          console.log(`🔍 DEBUG: Assignment ${assignment.id} status="${status}" doesn't match filter "${statusFilter}"`);
        } else {
          console.log(`🔍 DEBUG: ✅ Assignment ${assignment.id} status="${status}" MATCHES filter "${statusFilter}"`);
        }
        return matches;
      });
      console.log(`🔍 DEBUG: Status filter resulted in ${filtered.length} assignments (was ${beforeCount})`);
    }

    // DEBUG: Log all filtered assignment statuses
    console.log('🔍 DEBUG: Post-filter assignment statuses:');
    filtered.forEach((assignment, idx) => {
      console.log(`Filtered Assignment ${idx}: ID=${assignment.id}, status="${assignment.status}", storeName="${assignment.storeName}"`);
    });

    return filtered;
  }, [assignments, selectedOperator, statusFilter, includePending]);

  // Handle admin manual sign-off - updated for task-based interface
  const handleAdminSignOff = (assignment: LiveAssignment) => {
    // Use the assignment ID as the task ID since each assignment represents a campaign task
    const taskId = assignment.id;

    // Route to correct modal based on task status
    const visitData = {
      taskId: taskId, // Use assignment ID as task ID for backend API
      visitId: assignment.id, // Keep for compatibility
      storeId: assignment.storeId,
      operatorId: assignment.operatorId,
      campaignId: assignment.campaignId,
      storeName: assignment.storeName,
      operatorName: assignment.operatorName,
      taskName: assignment.taskName,
      currentStatus: assignment.status,
      mediaRequirements: assignment.mediaRequirements || [],
      currentNotes: assignment.postingEvents?.[0]?.notes,
      installerNote: assignment.postingEvents?.[0]?.installerNote,
      currentSignatureUrl: undefined, // Will be populated from evidence assets
      currentDiscrepancy: undefined
    };

    if (assignment.status === 'operator_complete') {
      // For operator submissions - use approval modal
      setApproveOperatorModal({
        isOpen: true,
        visitData: visitData
      });
    } else {
      // For manual admin tasks - use manual sign-off modal
      setAdminManualSignOffModal({
        isOpen: true,
        visitData: visitData
      });
    }
  };

  // Handle proof editing
  const handleEditProof = (visitId: number) => {
    setProofEditModal({
      isOpen: true,
      visitId
    });
  };

  // Handle view details
  const handleViewDetails = (storeId: number, operatorId?: number, assignmentId?: number) => {
    console.log('Opening enhanced proof details for store:', storeId, assignmentId ? `(Assignment ${assignmentId})` : '(All assignments)');
    setEnhancedProofModal({
      isOpen: true,
      storeId: storeId,
      cycleId: parseInt(selectedCycle),
      operatorId: operatorId,
      assignmentId: assignmentId // ✅ Pass assignmentId to filter to specific assignment
    });
  };

  // Handle admin manual submission
  const handleAdminManualSubmission = (assignment: any) => {
    setAdminSubmissionModal({
      isOpen: true,
      assignment: {
        id: assignment.id, // Pass the actual assignment ID for the API endpoint
        taskId: assignment.taskId, // Use correct taskId field
        storeId: assignment.storeId,
        storeName: assignment.storeName,
        storeAddress: assignment.storeAddress || 'TBC',
        operatorId: assignment.operatorId, // Use operatorId from live API
        userId: assignment.userId || assignment.operatorId, // Fallback to operatorId if userId is null
        operatorName: assignment.operatorName,
        campaignId: assignment.campaignId,
        campaignName: assignment.campaignName,
        taskName: assignment.taskName, // Use taskName instead of packName
        cycleId: parseInt(selectedCycle),
        status: assignment.status,
        targetUnits: assignment.targetUnits || 0,
        mediaRequirements: assignment.mediaRequirements || []
      }
    });
  };

  // Success handlers - updated for live API integration
  const handleSuccess = () => {
    console.log('🔄 handleSuccess called - invalidating live assignment queries for cycle:', selectedCycle);

    // Invalidate the live assignment query to get real-time updates
    queryClient.invalidateQueries({
      queryKey: ['/api/live-status/assignments', selectedCycle]
    });

    // Also refetch immediately for instant UI updates
    refetchStores();

    // Invalidate other related queries that might be affected
    queryClient.invalidateQueries({ queryKey: ['/api/mobile/ready-for-admin-signoff'] });
    queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });

    console.log('✅ Live assignment cache invalidation completed');
  };

  // Memoized handlers for table columns
  const handleAdminSignOffCallback = useCallback(handleAdminSignOff, []);
  const handleViewDetailsCallback = useCallback(handleViewDetails, [selectedCycle]);
  const getCycleNameCallback = useCallback(getCycleName, [cycles, selectedCycle]);
  const validateOperatorSignatureCallback = useCallback(validateOperatorSignature, []);

  // Create columns
  const columns = useMemo(
    () =>
      createProofOfPostingColumns({
        onAdminSignOff: handleAdminSignOffCallback,
        onViewDetails: handleViewDetailsCallback,
        getCycleName: getCycleNameCallback,
        validateOperatorSignature: validateOperatorSignatureCallback,
      }),
    [handleAdminSignOffCallback, handleViewDetailsCallback, getCycleNameCallback, validateOperatorSignatureCallback]
  );

  const heading = sectionHeading(activeSection);

  const renderSectionBody = () => {
    switch (activeSection) {
      case 'proof':
        return selectedCycle ? (
          <WhatWasPostedTable
            retailerId={parseInt(selectedRetailer)}
            cycleId={parseInt(selectedCycle)}
            operators={operators}
          />
        ) : null;
      case 'compliance':
        return selectedCycle ? (
          <Tabs defaultValue="detailed">
            <TabsList>
              <TabsTrigger value="detailed">Compliance Detailed</TabsTrigger>
              <TabsTrigger value="overview">Compliance Overview</TabsTrigger>
              <TabsTrigger value="install-status">Install status</TabsTrigger>
            </TabsList>

            <TabsContent value="detailed" className="mt-4">
              <ComplianceDetailedGrid
                data={complianceData}
                isLoading={complianceLoading}
                error={complianceError as Error | null}
              />
            </TabsContent>

            <TabsContent value="overview" className="mt-4">
              <ComplianceOverviewGrid
                data={complianceData}
                isLoading={complianceLoading}
                error={complianceError as Error | null}
              />
            </TabsContent>

            <TabsContent value="install-status" className="mt-4">
              <InstallStatusPanel
                retailerId={parseInt(selectedRetailer, 10)}
                cycleId={parseInt(selectedCycle, 10)}
                campaignId={
                  selectedComplianceCampaign === 'all'
                    ? null
                    : parseInt(selectedComplianceCampaign, 10)
                }
                storeId={selectedStoreId}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Select a retailer and cycle to load compliance data.
            </CardContent>
          </Card>
        );
      case 'reviews':
        return selectedCycle ? (
          <Tabs defaultValue="rejected">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
                <TabsTrigger value="campaigns">By campaign</TabsTrigger>
                <TabsTrigger value="installers">By installer</TabsTrigger>
              </TabsList>
              <Tabs
                value={reviewTaskKind}
                onValueChange={(value) => setReviewTaskKind(value as ReviewTaskKind)}
              >
                <TabsList>
                  <TabsTrigger value="both">Both</TabsTrigger>
                  <TabsTrigger value="posting">Postings</TabsTrigger>
                  <TabsTrigger value="removal">Removals</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <TabsContent value="rejected" className="mt-4">
              <SubmissionReviewReportPanel
                view="rejected"
                taskKind={reviewTaskKind}
                retailerId={parseInt(selectedRetailer, 10)}
                cycleId={parseInt(selectedCycle, 10)}
                campaignId={
                  selectedComplianceCampaign === 'all'
                    ? null
                    : parseInt(selectedComplianceCampaign, 10)
                }
                storeId={selectedStoreId}
              />
            </TabsContent>

            <TabsContent value="campaigns" className="mt-4">
              <SubmissionReviewReportPanel
                view="campaigns"
                taskKind={reviewTaskKind}
                retailerId={parseInt(selectedRetailer, 10)}
                cycleId={parseInt(selectedCycle, 10)}
                campaignId={
                  selectedComplianceCampaign === 'all'
                    ? null
                    : parseInt(selectedComplianceCampaign, 10)
                }
                storeId={selectedStoreId}
              />
            </TabsContent>

            <TabsContent value="installers" className="mt-4">
              <SubmissionReviewReportPanel
                view="installers"
                taskKind={reviewTaskKind}
                retailerId={parseInt(selectedRetailer, 10)}
                cycleId={parseInt(selectedCycle, 10)}
                campaignId={
                  selectedComplianceCampaign === 'all'
                    ? null
                    : parseInt(selectedComplianceCampaign, 10)
                }
                storeId={selectedStoreId}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Select a retailer and cycle to load reviews.
            </CardContent>
          </Card>
        );
      default: {
        const _exhaustive: never = activeSection;
        return _exhaustive;
      }
    }
  };

  return (
    <div className="proof-v2-view container mx-auto p-6">
      {/* Print-specific font/spacing reductions to match Sign-off (-1pt, tighter spacing) */}
      <style>{`
        @media print {
          .proof-v2-view { font-size: 9pt; line-height: 1.35; }
          .proof-v2-view h1 { font-size: 15pt; margin-bottom: 6px; }
          .proof-v2-view h2 { font-size: 13pt; margin-bottom: 6px; }
          .proof-v2-view h3 { font-size: 11pt; margin-bottom: 6px; }
          .proof-v2-view .space-y-6 > * + * { margin-top: 16px !important; }
          .proof-v2-view table { border-collapse: collapse; width: 100%; }
          .proof-v2-view table th,
          .proof-v2-view table td { padding: 6px !important; font-size: 9pt !important; line-height: 1.35; }
          .proof-v2-view table th { padding: 6px !important; font-size: 9pt !important; line-height: 1.35; }
          .proof-v2-view .card { padding: 8px !important; }
        }
      `}</style>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{heading.title}</h1>
            <p className="text-gray-600">{heading.subtitle}</p>
          </div>
          <Button
            onClick={() => refetchStores()}
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Year Filter */}
              <div className="pb-4 border-b">
                <YearFilter />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Retailer */}
                <div className="space-y-2">
                  <Label>Retailer</Label>
                  <Select value={selectedRetailer} onValueChange={(v) => {
                    setSelectedRetailer(v);
                    setSelectedCycle(''); // Reset cycle when retailer changes
                    hasAutoSelectedCycle.current = false; // Allow auto-select for new retailer
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select retailer" />
                    </SelectTrigger>
                    <SelectContent>
                      {retailers.map((retailer) => (
                        <SelectItem key={retailer.id} value={retailer.id.toString()}>
                          {retailer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cycle */}
                <div className="space-y-2">
                  <Label>Booking Cycle</Label>
                  <Select
                    value={selectedCycle}
                    onValueChange={setSelectedCycle}
                    disabled={!selectedRetailer}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      {cycles.map((cycle) => (
                        <SelectItem key={cycle.id} value={cycle.id.toString()}>
                          {cycle.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {activeSection === 'proof' ? (
                  <>
                    {/* Operator */}
                    <div className="space-y-2">
                      <Label>Operator</Label>
                      <Select value={selectedOperator} onValueChange={setSelectedOperator}>
                        <SelectTrigger>
                          <SelectValue placeholder="All operators" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Operators</SelectItem>
                          {operators.map((operator) => (
                            <SelectItem key={operator.id} value={operator.id.toString()}>
                              {operator.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status Filter - Phase 2: Canonical Statuses Only */}
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="pending">⏱️ Pending</SelectItem>
                          <SelectItem value="operator_complete">⏳ Operator Complete</SelectItem>
                          <SelectItem value="admin_complete">✅ Admin Complete</SelectItem>
                          <SelectItem value="admin_rejected">❌ Admin Rejected</SelectItem>
                          <SelectItem value="locked">🔒 Locked</SelectItem>
                          <SelectItem value="archived">📦 Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Campaign</Label>
                      <Select
                        value={selectedComplianceCampaign}
                        onValueChange={setSelectedComplianceCampaign}
                        disabled={!selectedCycle || complianceAllCampaignsQuery.isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All campaigns" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All campaigns</SelectItem>
                          {(complianceAllCampaignsQuery.data?.campaigns || []).map((campaign) => (
                            <SelectItem key={campaign.campaignId} value={String(campaign.campaignId)}>
                              {campaign.campaignName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Store</Label>
                      <Select
                        value={selectedStore}
                        onValueChange={setSelectedStore}
                        disabled={!selectedCycle || complianceAllCampaignsQuery.isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All stores" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All stores</SelectItem>
                          {cycleStores.map((store) => (
                            <SelectItem key={store.id} value={String(store.id)}>
                              {store.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>

              {activeSection === 'proof' && (
                <>
                  {/* Include Pending Tasks Toggle */}
                  <div className="flex items-center space-x-3 pt-2 border-t">
                    <input
                      id="include-pending"
                      type="checkbox"
                      checked={includePending}
                      onChange={(e) => setIncludePending(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      title="Include pending tasks awaiting operator submission"
                    />
                    <Label
                      htmlFor="include-pending"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Include pending tasks awaiting operator submission
                    </Label>
                    <Badge variant="secondary" className="text-xs">
                      {includePending ? 'Showing All' : 'Completed Only'}
                    </Badge>
                  </div>

                  {/* Include Archived Records Toggle */}
                  <div className="flex items-center space-x-3 pt-2 border-t">
                    <input
                      id="include-archived"
                      type="checkbox"
                      checked={includeArchived}
                      onChange={(e) => setIncludeArchived(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      title="Include archived records"
                    />
                    <Label
                      htmlFor="include-archived"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Include archived records
                    </Label>
                    <Badge variant="secondary" className="text-xs">
                      {includeArchived ? 'Including Archived' : 'Active Only'}
                    </Badge>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {renderSectionBody()}

        {/* Modals */}
        <AdminManualSignOffModal
          isOpen={adminManualSignOffModal.isOpen}
          onClose={() => setAdminManualSignOffModal({ isOpen: false })}
          visitData={adminManualSignOffModal.visitData}
          onSuccess={handleSuccess}
        />

        <ApproveOperatorSubmissionModal
          isOpen={approveOperatorModal.isOpen}
          onClose={() => setApproveOperatorModal({ isOpen: false })}
          visitData={approveOperatorModal.visitData}
          onSuccess={handleSuccess}
        />

        <ProofEditModal
          isOpen={proofEditModal.isOpen}
          onClose={() => setProofEditModal({ isOpen: false })}
          visitId={proofEditModal.visitId!}
          onSuccess={handleSuccess}
        />

        <EnhancedProofModal
          isOpen={enhancedProofModal.isOpen}
          onClose={() => setEnhancedProofModal({ isOpen: false })}
          storeId={enhancedProofModal.storeId || 0}
          cycleId={enhancedProofModal.cycleId || 0}
          operatorId={enhancedProofModal.operatorId}
          assignmentId={enhancedProofModal.assignmentId} // ✅ Pass assignmentId to filter modal
          onSuccess={handleSuccess}
          refetch={refetchStores}
        />

        <AdminManualSubmissionModal
          isOpen={adminSubmissionModal.isOpen}
          onClose={() => setAdminSubmissionModal({ isOpen: false })}
          assignment={adminSubmissionModal.assignment}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
}
